export const MONITOR_TYPES = [
  'invoice',
  'approval',
  'contract',
  'delivery',
  'refund',
  'reply',
  'custom',
  'pull_request',
] as const;
export type MonitorType = (typeof MONITOR_TYPES)[number];
export type MonitorState =
  | 'suggested'
  | 'watching'
  | 'needs_you'
  | 'paused'
  | 'done'
  | 'expired'
  | 'stopped'
  | 'dismissed'
  | 'cleared';
export type MonitorNotifyPolicy =
  | 'on_problem_and_done'
  | 'every_change'
  | 'only_done';
export const SOURCE_HEALTH_VALUES = ['ok', 'failing'] as const;
export type SourceHealth = (typeof SOURCE_HEALTH_VALUES)[number];
// Mapped from a read failure (web fetch status/network error, or an email
// thread with no accessible messages) so the UI can show a cause and, for
// needs_sign_in, a reconnect action, without exposing the raw error text.
export const SOURCE_FAILING_REASONS = [
  'needs_sign_in',
  'not_found',
  'blocked',
  'unreachable',
  'unreadable',
  'mailbox_unavailable',
] as const;
export type SourceFailingReason = (typeof SOURCE_FAILING_REASONS)[number];
// A page that is gone or sits behind a sign-in will not come back by retrying;
// only the owner editing the sources can change the verdict.
export const SOURCE_PERMANENT_FAILING_REASONS: readonly SourceFailingReason[] =
  ['needs_sign_in', 'not_found'];
export enum MonitorSourceKind {
  EMAIL_THREAD = 'email_thread',
  WEB_PAGE = 'web_page',
  PULL_REQUEST = 'pull_request',
  DOBOX_BOARD = 'dobox_board',
  MCP_CONNECTION = 'mcp_connection',
}
export const MONITOR_SOURCE_KINDS: readonly MonitorSourceKind[] =
  Object.values(MonitorSourceKind);
// Kinds only an agent watcher accepts: they need its config (scope, tool loop).
export const MONITOR_AGENT_ONLY_SOURCE_KINDS: readonly MonitorSourceKind[] = [
  MonitorSourceKind.DOBOX_BOARD,
  MonitorSourceKind.MCP_CONNECTION,
];
// Kinds a simple (non-agent) monitor accepts.
export const MONITOR_SIMPLE_SOURCE_KINDS: readonly MonitorSourceKind[] =
  MONITOR_SOURCE_KINDS.filter(
    (kind) => !MONITOR_AGENT_ONLY_SOURCE_KINDS.includes(kind),
  );
export type MonitorSource = {
  // dobox_board: ref = taskGroupId. mcp_connection: ref = the MCP connectionId.
  kind: MonitorSourceKind;
  ref: string;
  label: string;
  optional?: boolean;
  // Server-populated only (never set from client input); preserved across
  // edits by matching the normalized ref so a rename doesn't reset health.
  name?: string;
  detail?: string;
  health?: SourceHealth;
  failingSince?: number | null;
  failingReason?: SourceFailingReason | null;
  // Set once a permanent failure is recorded; later checks skip the source
  // until the owner edits the sources.
  unreadable?: boolean;
  // email_thread only: the sender's display name (label holds the address) and
  // every thread of that sender followed for this watcher, `ref` first.
  senderName?: string;
  threadRefs?: string[];
};
export type MonitorEvent = {
  id: string;
  at: number;
  kind:
    | 'suggested'
    | 'started'
    | 'check_no_change'
    | 'change'
    | 'needs_you'
    | 'done'
    | 'expired'
    | 'user_action'
    | 'check_failed'
    | 'source_failing'
    | 'source_recovered'
    // Automatic dismissal only — a manual dismiss from the user is 'user_action'.
    | 'dismissed';
  summary: string;
  source: MonitorSource | null;
  stageAfter: number | null;
};
// The AI's read of the monitor's current state, kept as short structured
// fields instead of a paragraph so the UI never shows the same summary twice.
export type MonitorSnapshot = {
  headline: string;
  points: string[];
  needsUser: boolean;
  doneReason: string | null;
};
export const MONITOR_WEEKDAYS = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
] as const;
export type MonitorWeekday = (typeof MONITOR_WEEKDAYS)[number];
export const MONITOR_RESULT_SHAPES = [
  'items',
  'metric',
  'change',
  'custom',
] as const;
export type MonitorResultShape = (typeof MONITOR_RESULT_SHAPES)[number];
// The detail view is data, never code: an ordered list of blocks from this
// closed vocabulary. The three standard shapes use fixed presets; only
// 'custom' carries a model-chosen list, validated against the same vocabulary.
export const MONITOR_VIEW_BLOCK_KINDS = [
  'kpis',
  'item_list',
  'metric_chart',
  'stats',
  'before_after',
  'points',
  'key_values',
] as const;
export type MonitorViewBlockKind = (typeof MONITOR_VIEW_BLOCK_KINDS)[number];
export type MonitorViewBlock = {
  kind: MonitorViewBlockKind;
  title: string | null;
};
export const MONITOR_VIEW_PRESETS: Record<
  Exclude<MonitorResultShape, 'custom'>,
  MonitorViewBlock[]
> = {
  items: [
    { kind: 'kpis', title: null },
    { kind: 'item_list', title: null },
  ],
  metric: [
    { kind: 'metric_chart', title: null },
    { kind: 'stats', title: null },
  ],
  change: [{ kind: 'before_after', title: null }],
};
export const MONITOR_VIEW_MAX_BLOCKS = 4;
export const MONITOR_VIEW_TITLE_MAX_LENGTH = 60;
export const MONITOR_TRIGGER_KINDS = [
  'stale',
  'pace',
  'before_deadline',
  'threshold',
  'change_pct',
  'on_date',
  'any_change',
  'section_change',
] as const;
export type MonitorTriggerKind = (typeof MONITOR_TRIGGER_KINDS)[number];
export type MonitorTrigger = { id: string } & (
  | { kind: 'stale'; days: number }
  | { kind: 'pace'; byDate: number }
  | { kind: 'before_deadline'; days: number }
  | { kind: 'threshold'; op: 'below' | 'above'; value: number }
  | { kind: 'change_pct'; pct: number; window: '1d' | '1w' }
  | { kind: 'on_date'; date: number; message: string }
  | { kind: 'any_change' }
  | { kind: 'section_change'; hint: string }
);
export type MonitorMeasure = { label: string; unit: string; decimals: number };
export type MonitorItemScope = {
  taskGroupId: string;
  subgroupIds: string[];
  includeLinkedPullRequests: boolean;
};
export type MonitorChangeTarget = { url: string; selectorHint: string | null };
export type MonitorDigest = {
  cadence: 'daily' | 'weekly';
  weekday: MonitorWeekday | null;
  hour: number;
};
export type MonitorAgentConfig = {
  shape: MonitorResultShape;
  // The preset for the standard shapes.
  view: MonitorViewBlock[];
  // Required when shape = 'metric'.
  measure: MonitorMeasure | null;
  // Required when shape = 'items' and a DoBox source is present.
  scope: MonitorItemScope | null;
  // Required when shape = 'change'.
  target: MonitorChangeTarget | null;
  triggers: MonitorTrigger[];
  // null = every day.
  activeDays: MonitorWeekday[] | null;
  digest: MonitorDigest | null;
  // The goal's own target date, distinct from expiresAt (the safety stop).
  deadline: number | null;
};
export type MonitorTrackedItem = {
  id: string;
  title: string;
  stage: string | null;
  owner: string | null;
  lastChange: string;
  risk: 'ok' | 'at_risk';
};
export type MonitorLabelValue = { label: string; value: string };
export type MonitorSeriesPoint = { at: number; value: number };
export type MonitorAgentSnapshot = {
  at: number;
  // max 3
  kpis: MonitorLabelValue[];
  // at-risk first; capped at MONITOR_AGENT_ITEMS_MAX
  items: MonitorTrackedItem[];
  value: number | null;
  // last ~24 points, for the list row
  sparkline: number[];
  // max 3
  stats: MonitorLabelValue[];
  before: string | null;
  after: string | null;
  changedAt: number | null;
  // for 'key_values'
  fields: MonitorLabelValue[];
  // triggers whose condition currently holds (dedupe + amber state)
  firedTriggerIds: string[];
  // when the last digest summary went out; absent before the first one
  lastDigestAt?: number | null;
};
export const MONITOR_AGENT_ITEMS_MAX = 50;
// Running agent watchers per account; each run costs model calls.
export const MONITOR_AGENT_RUNNING_MAX = 20;
// A watcher that reads an MCP connection runs a tool loop each time.
export const MONITOR_AGENT_MCP_MIN_CADENCE_MINUTES = 240;
// Event triggers fire on every run whose validated change is real; they are
// never carried as a held condition.
export const MONITOR_EVENT_TRIGGER_KINDS = [
  'any_change',
  'section_change',
] as const;
// Monitors that count against the per-account limit of 100.
export const MONITOR_LIVE_STATES: readonly MonitorState[] = [
  'watching',
  'needs_you',
  'suggested',
  'paused',
];
// States whose schedule is live: the executor runs them.
export const MONITOR_RUNNING_STATES: readonly MonitorState[] = [
  'watching',
  'needs_you',
];
// Actions that move a monitor into a running state.
export const MONITOR_RUNNING_ACTIONS: readonly MonitorAction[] = [
  'start',
  'resume',
  'rewatch',
];
// Actions that may carry an agent config.
export const MONITOR_AGENT_EDIT_ACTIONS: readonly MonitorAction[] = [
  'update',
  'resume',
  'rewatch',
];
// Usage-attribution sub-feature per monitoring model call path, so the cost of
// each path is measurable on its own.
export const MONITOR_AI_SUB_FEATURES = {
  DETECTION: 'monitoring-detection',
  OBSERVATION: 'monitoring-observation',
  SNAPSHOT: 'monitoring-snapshot',
  AGENT_SUGGESTION: 'monitoring-agent-suggestion',
  MCP_COLLECTOR: 'monitoring-mcp-collector',
} as const;
export const MONITOR_AGENT_TRIGGERS_MAX = 8;
export const MONITOR_SERIES_MAX_POINTS = 1000;
export type DoBoxMonitor = {
  id: string;
  userId: string;
  taskGroupId: string;
  type: MonitorType;
  title: string;
  goal: string;
  // The end state phrased as a state, never a task: "until delivered", never
  // "Review and approve pull request". Set from detection at suggestion time;
  // null for monitors created directly by the user.
  condition: string | null;
  state: MonitorState;
  health: 'on_track' | 'at_risk' | null;
  stages: string[];
  currentStage: number | null;
  keyFact: string | null;
  // Kept populated as a backward-compat mirror of snapshot.headline — the web
  // app still reads this field directly.
  lastUpdate: string | null;
  snapshot: MonitorSnapshot | null;
  // An agent watcher is a 'custom' monitor with a non-null agent config.
  agent: MonitorAgentConfig | null;
  agentSnapshot: MonitorAgentSnapshot | null;
  sources: MonitorSource[];
  cadenceMinutes: number;
  notifyPolicy: MonitorNotifyPolicy;
  expiresAt: number;
  originEmailId: string | null;
  // The DoBox entry a watcher was created from; separate from linkedTodoId, which
  // the store resets and uses for the monitor's own To do.
  originEntryId?: string | null;
  reference: string | null;
  communicationIds: string[];
  linkedTodoId: string | null;
  lastCheckedAt: number | null;
  nextCheckAt: number | null;
  createdAt: number;
  closedAt: number | null;
  version: number;
};
// What the create screen gets back for a goal sentence. Never persisted: the
// client sends it through createDoBoxMonitor(type: 'custom', settings, agent).
export type MonitorAgentSuggestion = {
  title: string;
  agent: MonitorAgentConfig;
  sources: MonitorSource[];
  cadenceMinutes: number;
  expiresAt: number;
  // Tools the goal needs that are not connected, by plain name.
  missingConnections: string[];
};
export type MonitorSettings = Pick<
  DoBoxMonitor,
  'title' | 'goal' | 'sources' | 'cadenceMinutes' | 'notifyPolicy' | 'expiresAt'
>;
// States a user can sweep into 'cleared' ('Finished' list housekeeping).
export const MONITOR_CLEARABLE_STATES: readonly MonitorState[] = [
  'done',
  'expired',
  'stopped',
];
export type MonitorAction =
  | 'start'
  | 'pause'
  | 'resume'
  | 'stop'
  | 'dismiss'
  | 'check'
  | 'update'
  | 'clear'
  | 'rewatch';
// A suggestion that nobody accepts or dismisses expires at createdAt + this
// many days — much sooner than the template's own safety deadline, which
// only starts counting once the user accepts it (see MONITOR_TEMPLATES below).
export const MONITOR_SUGGESTION_EXPIRY_DAYS = 7;
// How often an unaccepted suggestion is re-checked against its sources while
// it waits — a "light check" that only ever dismisses or reschedules, never
// notifies or creates a To do.
export const MONITOR_SUGGESTION_LIGHT_CHECK_MINUTES = 1440;
// At least one of senderDomain/type/text must be set (enforced server-side).
// senderDomain is normalized lowercase, no "www." prefix. text is user-authored
// free text, capped at MONITOR_SUGGESTION_RULE_TEXT_MAX_LENGTH.
export type MonitorSuggestionRule = {
  id: string;
  senderDomain: string | null;
  type: MonitorType | null;
  text: string | null;
  createdAt: number;
};
export const MONITOR_SUGGESTION_RULE_MAX_PER_USER = 20;
export const MONITOR_SUGGESTION_RULE_TEXT_MAX_LENGTH = 200;
export const MONITOR_TEMPLATES: Record<
  MonitorType,
  { stages: string[]; cadenceMinutes: number; safetyDays: number }
> = {
  invoice: {
    stages: ['sent', 'received', 'approved', 'scheduled', 'paid'],
    cadenceMinutes: 1440,
    safetyDays: 45,
  },
  approval: {
    stages: ['requested', 'seen', 'decided'],
    cadenceMinutes: 480,
    safetyDays: 30,
  },
  contract: {
    stages: ['sent', 'partially_signed', 'signed'],
    cadenceMinutes: 720,
    safetyDays: 60,
  },
  delivery: {
    stages: [
      'ordered',
      'shipped',
      'in_transit',
      'out_for_delivery',
      'delivered',
    ],
    cadenceMinutes: 480,
    safetyDays: 45,
  },
  refund: { stages: [], cadenceMinutes: 1440, safetyDays: 45 },
  reply: { stages: [], cadenceMinutes: 1440, safetyDays: 30 },
  custom: { stages: [], cadenceMinutes: 1440, safetyDays: 30 },
  pull_request: {
    stages: ['open', 'checks_passing', 'approved', 'merged'],
    cadenceMinutes: 60,
    safetyDays: 14,
  },
};
